import React, {ChangeEvent, useCallback, useEffect, useState} from "react";
import {apiGet} from "../../services/apiService";
import {useAuth} from "../../providers/AuthProvider";
import {useSiteMessage} from "../../providers/SiteMessageProvider";
import {OverflowStealthScroll, SiteMessageEnum} from "@varymadellc/my-weed-club-component-library";
import {Account} from "@/types";
import {Input} from "@varymadellc/my-weed-club-component-library";
import Page from "@/layout/Page";
import ProfileItem from "../../layout/ProfileItem";
import MembersPageNav from "../../layout/nav/MembersPageNav";
import Loading from "@/components/Loading/Loading";

export default function MembersPage() {
    const [loading, setLoading] = useState<boolean>(false);

    const [search, setSearch] = useState<string>('');

    const {setMessage} = useSiteMessage();

    const {authToken} = useAuth();

    const [profiles, setProfiles] = useState<any[]>();

    const fetchAccount = useCallback(async () => {
        try {
            const response = await apiGet('/public/account', authToken);

            if (!response.ok) {
                setMessage(response.statusText, SiteMessageEnum.error);
                return;
            }

            const json = await response.json();

            if (json?.error) {
                setMessage(json.error, SiteMessageEnum.error);
                return;
            }

            setProfiles(sortByPhotosFirst(json));
        } catch (error: any) {
            console.log('error', error);
            setMessage('We are working on loading the members, but there is delay. Please try again later.', SiteMessageEnum.error);
        }

    }, [authToken, setMessage]);

    useEffect(() => {
        (async () => {
            if (!authToken) {
                return;
            }

            setLoading(true);

            await fetchAccount();

            setLoading(false);
        })();
    }, [fetchAccount, authToken]);

    const searchHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const {target} = event

        const {value = ''} = target;

        setSearch(value);
    };

    const sortFunc = (a: Account, b: Account) => {
        if (!a.createdAt || !b.createdAt) {
            return 0;
        }

        if (a.createdAt > b.createdAt) {
            return -1;
        }

        if (a.createdAt < b.createdAt) {
            return 1;
        }

        return 0;
    };

    // TODO fix so that it doens't reload the profiles after each search. You should hide them vs remove them
    const filterProfilesBySearch = (search: string = '', profiles: Account[]) => {
        return profiles.filter((profile: Account) => {
            const foundUsername = profile.username.toLowerCase().indexOf(search.toLowerCase()) !== -1;

            const foundByTags = profile.metadata?.badges?.some((badge) => {
                return badge.toLowerCase().indexOf(search.toLowerCase()) !== -1;
            });

            return foundUsername || foundByTags;
        });
    }

    const deleteProfileCallback = (uuid: string) => {
        if (profiles) {
            setProfiles([...profiles.filter((profile) => profile.uuid !== uuid)]);
        }
    }

    const setSortOrderHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        const {target} = event;

        if (!target) return;

        const {value} = target;

        if (profiles && value !== 'null') {
            setProfiles([...profiles.sort((a, b) => {
                if (value === 'newest') {
                    return sortFunc(a, b);
                }

                return sortFunc(b, a);
            })]);
        } else if (profiles && value === 'null') {
            const sortedProfiles = sortByPhotosFirst(profiles || []);

            setProfiles([...sortedProfiles]);
        }
    }

    const sortByPhotosFirstSorter = (a, b) => {
        if (a?.file?.length > 0) {
            return -1;
        }

        if (b?.file?.length > 0) {
            return 1;
        }

        return 0;
    }

    const sortByCreatedDate = (a, b) => {
        return (a.createdAt < b.createdAt) ? -1 : 1;
    }

    const sortByPhotosFirst = (profiles) => {
        return profiles.sort(sortByCreatedDate).sort(sortByPhotosFirstSorter);
    }

    const displayableProfiles = (search && search.length > 0 && profiles && profiles.length > 0) ? filterProfilesBySearch(search, profiles) : profiles;

    return (
        <Page title="Members"
              menu={<OverflowStealthScroll>
                  <Input placeholder="search name or tag..." name="search" type="text" onChange={searchHandler}
                         value={search}/>
                  <MembersPageNav numberOfProfiles={profiles?.length || 0}
                                  setSortOrderHandler={setSortOrderHandler}/>
              </OverflowStealthScroll>
              }>
            {
                loading ?
                    <Loading className="mx-auto"/> :
                    (
                        <>
                            <div className="flex flex-row flex-wrap gap-2 justify-center">
                                {displayableProfiles && displayableProfiles.map((profile, index) => {
                                    return <ProfileItem profile={profile} key={profile.uuid}
                                                        deleteProfileCallback={deleteProfileCallback}/>;
                                })}
                            </div>
                        </>
                    )
            }
        </Page>
    );
}